/**
 * Teczka instalacji klienta prywatnego, część `miejsca`, dla klienta „Prosty audyt” (makieta „VoltValue dla domu”,
 * pakiet P7 z 2026-10-24): gdzie w domu są licznik, rozdzielnica główna, falownik i magazyn, jakie jest przyłącze,
 * którędy kable zejdą z dachu i czy przy falowniku jest Wi-Fi (`VV/zadania/2026-10_teczka-instalacji.md`, punkty 6 i 7).
 *
 * ZASADY (decyzje właściciela 2026-10-23 i 2026-10-24):
 * - przy każdym pytaniu jest „Nie wiem, wykonawca sprawdzi” (wartość `NIE_WIEM`); w tekstach dla klienta „wykonawca”;
 * - krótko: 10 pytań; pomieszczenie licznika i kondygnacje mówi opis słowami (pola zostają w kształcie, `pytamy: false`),
 *   kod PPE i operator są opcjonalne (dla formalności, nie dla wykonawcy na miejscu, `opcjonalne: true`);
 * - podstawą jest opis słowami („garaż, przy drzwiach”); zdjęcia to druga fala i formularz ich nie pokazuje;
 * - przy falowniku szacowana odległość od rozdzielnicy głównej: do 5, 10, 15 m, więcej albo „nie wiem”;
 * - strona świata tylko przy rozdzielnicy (trafia na plan dla ekip ratowniczych); licznik, falownik i magazyn bez niej;
 * - lista „do sprawdzenia na miejscu”: pola z „nie wiem” i bez odpowiedzi, punkty wynikające z odpowiedzi
 *   (słabe Wi-Fi, długa trasa, jedna faza, magazyn…) i stałe punkty (pokrycie i więźba, uziemienie, wymiary dachu).
 *
 * KSZTAŁT to umowa z zapisem teczki na koncie (sesja „Teczka: zapis na koncie”, opis w zadaniu, sekcja „Umowa
 * o danych teczki”): backend trzyma część jako JSON i nie sprawdza wnętrza, więc wczytanie zawsze przechodzi przez
 * `normalizujMiejsca`. Słowniki miejsc falownika i magazynu pochodzą z zawiadomienia do PSP i nie są tu zmieniane:
 * dzięki temu `miejscaDoPsp` przenosi odpowiedzi do formularza straży bez tłumaczenia kluczy.
 *
 * Kontekst sprzętu (`{ zMagazynem, mocModulowKw, mocFalownikaKw, mocMagazynuKw }`) daje część `sprzet`; bez niego
 * pytamy o magazyn, a punkty o mocy liczymy tylko z tego, co wiadomo.
 */
import { MIEJSCA_FALOWNIKA, MIEJSCA_MAGAZYNU, NAZWY_POMIESZCZEN } from '../zawiadomieniePsp'
import { STRONY } from '../planRatowniczy'
import { OSD } from '../../config/zgloszeniaOsd'
import { liczbaZPola } from '../../config/klienci'
import { liczbaPl } from '../zgloszenieOsd'
import { osdZPpe } from '../operatorOsd'
import { ocenMocy, mnoznikMagazynu, LIMIT_JEDNOFAZOWY_KW } from '../mocPrzylaczeniowa'

export const WERSJA_MIEJSC = 1
export const NIE_WIEM = 'nie_wiem'
export const ETYKIETA_NIE_WIEM = 'Nie wiem, wykonawca sprawdzi'

const zDuzej = (t) => t.charAt(0).toUpperCase() + t.slice(1)

export const MIEJSCA_LICZNIKA = [
  ['ogrodzenie', 'W skrzynce na ogrodzeniu albo na granicy działki'],
  ['elewacja', 'W skrzynce na ścianie domu, od zewnątrz'],
  ['wewnatrz', 'W środku budynku'],
]

/** Pomieszczenia licznika i rozdzielnicy: klucze i nazwy z planu dla ekip ratowniczych, do tego korytarz. */
export const POMIESZCZENIA = [
  ['korytarz', 'Korytarz lub przedpokój'],
  ...['garaz', 'kotlownia', 'piwnica', 'poddasze'].map((k) => [k, zDuzej(NAZWY_POMIESZCZEN[k])]),
  ['inne', 'Inne pomieszczenie'],
]

export const KONDYGNACJE = [['piwnica', 'Piwnica'], ['parter', 'Parter'], ['pietro', 'Piętro'], ['poddasze', 'Poddasze']]

/** Piwnica i poddasze same mówią, na której kondygnacji jest pomieszczenie; wtedy o kondygnację nie pytamy. */
export const kondygnacjaZPomieszczenia = (pomieszczenie) => (['piwnica', 'poddasze'].includes(pomieszczenie) ? pomieszczenie : null)

/** Ściana z rozdzielnicą: strony świata planu dla ekip ratowniczych (`planRozdzielnica`). */
export const SCIANY = STRONY.map(([k, l]) => [k, `Ściana ${l}`])

export const OPCJE_FALOWNIKA = MIEJSCA_FALOWNIKA.map(([k, l]) => [k, zDuzej(l)])
export const OPCJE_MAGAZYNU = MIEJSCA_MAGAZYNU.map(([k, l]) => [k, zDuzej(l)])

export const ODLEGLOSCI = [['do_5', 'Do 5 m'], ['do_10', 'Do 10 m'], ['do_15', 'Do 15 m'], ['wiecej', 'Więcej niż 15 m']]
export const FAZY = [['3', 'Trzy fazy'], ['1', 'Jedna faza']]
export const OPERATORZY = [...OSD.map((o) => [o.klucz, o.nazwa]), ['inny', 'Inny operator']]

export const DROGI_KABLI = [
  ['poddasze', 'Przez poddasze, do którego da się wejść'],
  ['kanal', 'Pustym, nieużywanym kanałem wentylacyjnym'],
  ['elewacja', 'Po ścianie domu, od zewnątrz'],
]

export const ZASIEG_WIFI = [['dobry', 'Tak, działa dobrze'], ['slaby', 'Jest, ale słabe'], ['brak', 'Nie ma']]

/** Objaśnienia dla laika, pokazywane przy polach. */
export const OBJASNIENIA = {
  licznik: 'Zwykle w szarej skrzynce na ogrodzeniu albo na ścianie domu, czasem w środku.',
  rozdzielnica: 'Skrzynka z rzędem bezpieczników dla całego domu, często podpisanych „kuchnia”, „gniazda”. '
    + 'Jeśli jest kilka, chodzi o tę, do której dochodzi prąd od licznika.',
  falownik: 'Urządzenie wielkości małej walizki, wisi na ścianie. Lubi suche miejsce, w którym latem nie jest bardzo gorąco.',
  odleglosc: 'Wystarczy szacunek, licząc drogą, którą da się przejść. Jeden krok to mniej więcej metr.',
  magazyn: 'Warunki montażu podaje producent magazynu, a wykonawca potwierdza je na miejscu.',
  fazy: 'Większość domów ma trzy fazy. Licznik trójfazowy ma na tabliczce zapis 3x230/400 V.',
  moc: 'Z umowy z operatorem albo z rachunku, np. 14 kW.',
  operator: 'Właściciel sieci i licznika, nie zawsze ten sam co sprzedawca prądu. Nazwa jest na rachunku.',
  ppe: '18 cyfr, zaczyna się od 590. Jest na rachunku za prąd. Po nim rozpoznamy operatora.',
  kable: 'Przewody z paneli muszą zejść z dachu do falownika.',
  wifi: 'Falownik wysyła dane o produkcji przez internet. Sprawdź telefonem w miejscu falownika.',
}

/** Pusta część `miejsca`. Każde pole wyboru i wpisu może mieć wartość `NIE_WIEM`; `opis` to opis słowami. */
export const PUSTE_MIEJSCA = {
  wersja: WERSJA_MIEJSC,
  licznik: { gdzie: '', pomieszczenie: '', kondygnacja: '', opis: '' },
  rozdzielnica: { pomieszczenie: '', kondygnacja: '', sciana: '', opis: '' },
  falownik: { miejsce: '', odleglosc: '', opis: '' },
  magazyn: { miejsce: '', opis: '' },
  przylacze: { fazy: '', moc: '', operator: '', ppe: '' },
  kable: { droga: '', opis: '' },
  wifi: { zasieg: '' },
}

/**
 * Pola z odpowiedzią do wyboru (`opcje`) albo do wpisania (`tekst`), w kolejności formularza.
 * `gdy(m, kontekst)`: kiedy pole obowiązuje; pole nieobowiązujące nie trafia ani do braków, ani na listę.
 * `pytamy: false`: pole zostaje w kształcie (starsze odpowiedzi), ale audyt o nie nie pyta.
 * `opcjonalne: true`: pytamy, ale brak odpowiedzi nie jest brakiem i nie idzie na listę dla wykonawcy.
 */
export const POLA = [
  { czesc: 'licznik', pole: 'gdzie', etykieta: 'Gdzie jest licznik', opcje: MIEJSCA_LICZNIKA },
  { czesc: 'licznik', pole: 'pomieszczenie', etykieta: 'Pomieszczenie z licznikiem', opcje: POMIESZCZENIA, pytamy: false },
  { czesc: 'licznik', pole: 'kondygnacja', etykieta: 'Kondygnacja z licznikiem', opcje: KONDYGNACJE, pytamy: false },
  { czesc: 'rozdzielnica', pole: 'pomieszczenie', etykieta: 'Pomieszczenie z rozdzielnicą główną', opcje: POMIESZCZENIA },
  { czesc: 'rozdzielnica', pole: 'kondygnacja', etykieta: 'Kondygnacja z rozdzielnicą główną', opcje: KONDYGNACJE, pytamy: false },
  { czesc: 'rozdzielnica', pole: 'sciana', etykieta: 'Ściana z rozdzielnicą główną', opcje: SCIANY },
  { czesc: 'falownik', pole: 'miejsce', etykieta: 'Miejsce na falownik', opcje: OPCJE_FALOWNIKA },
  { czesc: 'falownik', pole: 'odleglosc', etykieta: 'Odległość falownika od rozdzielnicy głównej', opcje: ODLEGLOSCI },
  { czesc: 'magazyn', pole: 'miejsce', etykieta: 'Miejsce na magazyn energii', opcje: OPCJE_MAGAZYNU,
    gdy: (m, k) => k.zMagazynem !== false },
  { czesc: 'przylacze', pole: 'fazy', etykieta: 'Liczba faz przyłącza', opcje: FAZY },
  { czesc: 'przylacze', pole: 'moc', etykieta: 'Moc przyłączeniowa z umowy', tekst: true },
  { czesc: 'przylacze', pole: 'operator', etykieta: 'Operator sieci', opcje: OPERATORZY, opcjonalne: true },
  { czesc: 'przylacze', pole: 'ppe', etykieta: 'Kod PPE', tekst: true, opcjonalne: true },
  { czesc: 'kable', pole: 'droga', etykieta: 'Droga kabli z dachu do falownika', opcje: DROGI_KABLI },
  { czesc: 'wifi', pole: 'zasieg', etykieta: 'Wi-Fi w miejscu falownika', opcje: ZASIEG_WIFI },
]

/** Wybory, przy których opis słowami jest obowiązkowy („inne pomieszczenie” samo nic nie mówi). */
const WYBOR_Z_OPISEM = [['licznik', 'pomieszczenie'], ['rozdzielnica', 'pomieszczenie'], ['falownik', 'miejsce'], ['magazyn', 'miejsce']]

const DLUGOSC_OPISU = 300
const pusty = (v) => v == null || String(v).trim() === ''
const klucz = (p) => `${p.czesc}.${p.pole}`
const nazwa = (opcje, v) => (opcje.find(([k]) => k === v) || [])[1] || ''

/**
 * Dowolny JSON (z konta, z przeglądarki, stary albo pusty) -> pełna część w obecnym kształcie. Nieznane klucze
 * i wartości spoza słowników znikają; teksty przycięte, żeby część zmieściła się w limicie zapisu.
 */
export const normalizujMiejsca = (json) => {
  const zrodlo = json && typeof json === 'object' ? json : {}
  const wynik = { wersja: WERSJA_MIEJSC }
  for (const [czesc, pola] of Object.entries(PUSTE_MIEJSCA)) {
    if (czesc === 'wersja') continue
    const z = zrodlo[czesc] && typeof zrodlo[czesc] === 'object' ? zrodlo[czesc] : {}
    wynik[czesc] = {}
    for (const pole of Object.keys(pola)) {
      const v = typeof z[pole] === 'string' ? z[pole] : ''
      const def = POLA.find((p) => p.czesc === czesc && p.pole === pole)
      if (def?.opcje) wynik[czesc][pole] = v === NIE_WIEM || def.opcje.some(([k]) => k === v) ? v : ''
      else wynik[czesc][pole] = v.slice(0, DLUGOSC_OPISU)
    }
  }
  return wynik
}

/** Pytania obowiązujące przy tych odpowiedziach (z opcjonalnymi). */
export const polaPytane = (m, kontekst = {}) => POLA.filter((p) => p.pytamy !== false && (!p.gdy || p.gdy(m, kontekst)))

/** Pytania, na które czekamy na odpowiedź: bez opcjonalnych. Z nich braki, licznik postępu i lista dla wykonawcy. */
export const polaAktywne = (m, kontekst = {}) => polaPytane(m, kontekst).filter((p) => !p.opcjonalne)

/** Moc przyłączeniowa w kW albo null („nie wiem”, puste, nie liczba, waty zamiast kW). */
export const mocPrzylaczeniowaKw = (m) => {
  const x = m.przylacze.moc === NIE_WIEM ? null : liczbaZPola(m.przylacze.moc)
  return x != null && x < 1000 ? x : null
}

const cyfryPpe = (ppe) => String(ppe || '').replace(/\s/g, '')
const ppePoprawne = (ppe) => /^590\d{15}$/.test(cyfryPpe(ppe))

/** Wpis jest, ale błędny (zły kod PPE, moc w watach): na kartce i liście traktujemy go jak brak odpowiedzi. */
const bledny = (m, p) => {
  const v = m[p.czesc][p.pole]
  if (pusty(v) || v === NIE_WIEM) return false
  if (p.czesc === 'przylacze' && p.pole === 'moc') return mocPrzylaczeniowaKw(m) == null
  if (p.czesc === 'przylacze' && p.pole === 'ppe') return !ppePoprawne(v)
  return false
}

/**
 * Sprawdzenie odpowiedzi: `bledy` (klucz pola -> komunikat; zły format albo sprzeczność), `braki` (klucze pól bez
 * odpowiedzi), `odpowiedziane` z `wszystkich` obowiązujących. Formularz zapisuje się zawsze; to tylko podpowiedź.
 */
export const walidujMiejsca = (miejsca, kontekst = {}) => {
  const m = normalizujMiejsca(miejsca)
  const aktywne = polaAktywne(m, kontekst)
  const braki = aktywne.filter((p) => pusty(m[p.czesc][p.pole])).map(klucz)
  const bledy = {}

  const moc = m.przylacze.moc
  if (!pusty(moc) && moc !== NIE_WIEM) {
    const x = liczbaZPola(moc)
    if (x == null) bledy['przylacze.moc'] = 'Wpisz liczbę w kW, np. 14.'
    else if (x >= 1000) bledy['przylacze.moc'] = 'To wygląda na waty. Wpisz moc w kW, np. 14.'
  }

  const ppe = cyfryPpe(m.przylacze.ppe)
  if (!pusty(ppe) && ppe !== NIE_WIEM) {
    if (/^PL/i.test(ppe)) bledy['przylacze.ppe'] = 'To stary kod sprzed zmiany numeracji. Nowy, 18-cyfrowy kod jest na aktualnym rachunku.'
    else if (!ppePoprawne(ppe)) bledy['przylacze.ppe'] = 'Kod PPE ma 18 cyfr i zaczyna się od 590.'
    else {
      const zPpe = osdZPpe(ppe)
      const op = m.przylacze.operator
      if (zPpe && op && op !== NIE_WIEM && op !== zPpe) {
        bledy['przylacze.operator'] = `Kod PPE należy do operatora ${nazwa(OPERATORZY, zPpe)}.`
      }
    }
  }

  for (const [czesc, pole] of WYBOR_Z_OPISEM) {
    const obowiazuje = aktywne.some((p) => p.czesc === czesc && p.pole === pole)
    if (obowiazuje && m[czesc][pole] === 'inne' && pusty(m[czesc].opis)) bledy[`${czesc}.opis`] = 'Napisz krótko, gdzie to jest.'
  }

  return { bledy, braki, odpowiedziane: aktywne.length - braki.length, wszystkich: aktywne.length }
}

/** Operator rozpoznany po kodzie PPE (do podpowiedzi w formularzu) albo null. */
export const operatorZPpe = (ppe) => (cyfryPpe(ppe) === NIE_WIEM ? null : osdZPpe(cyfryPpe(ppe)))

export const STALE_PUNKTY = [
  { klucz: 'stale.dach', tekst: 'Stan pokrycia dachu i więźby: czy dach uniesie instalację i nie wymaga naprawy przed montażem.' },
  { klucz: 'stale.uziemienie', tekst: 'Uziemienie budynku i instalacja odgromowa, jeśli jest.' },
  { klucz: 'stale.wymiary', tekst: 'Wymiary dachu z projektu: pomiar na miejscu.' },
]

/** Punkty wynikające z odpowiedzi (decyzja właściciela 2026-10-23: dopisywać). */
const punktyZOdpowiedzi = (m, kontekst) => {
  const punkty = []
  const dodaj = (k, tekst) => punkty.push({ klucz: `z_odpowiedzi.${k}`, tekst, powod: 'z_odpowiedzi' })

  if (m.falownik.miejsce === 'poddasze') dodaj('poddasze', 'Falownik na poddaszu: czy latem nie jest tam za gorąco i czy jest przewiew.')
  if (m.falownik.miejsce === 'sciana') dodaj('sciana', 'Falownik na ścianie zewnętrznej: miejsce w cieniu i osłonięte od deszczu.')
  if (m.falownik.odleglosc === 'wiecej') dodaj('odleglosc', 'Falownik ponad 15 m od rozdzielnicy głównej: przekrój przewodu i jego trasa.')
  if (kontekst.zMagazynem || (m.magazyn.miejsce && kontekst.zMagazynem !== false)) {
    dodaj('magazyn', 'Magazyn energii: warunki montażu z instrukcji producenta (temperatura, odstępy, wentylacja).')
  }

  const jednaFaza = m.przylacze.fazy === '1'
  const ocena = liczbaZPola(kontekst.mocModulowKw)
    ? ocenMocy({
      mocModulowKw: kontekst.mocModulowKw, mocFalownikaKw: kontekst.mocFalownikaKw, mocMagazynuKw: kontekst.mocMagazynuKw,
      zMagazynem: Boolean(kontekst.zMagazynem), mocPrzylaczeniowaKw: mocPrzylaczeniowaKw(m), jednofazowa: jednaFaza,
    }, mnoznikMagazynu(kontekst.dzis))
    : null
  if (ocena?.stan === 'gotowe') {
    if (ocena.jednofazowaPonad) {
      dodaj('fazy', `Przyłącze jednofazowe, a instalacja ma ${liczbaPl(ocena.lacznie)} kW: przy jednej fazie moc nie może przekroczyć `
        + `${liczbaPl(LIMIT_JEDNOFAZOWY_KW)} kW. Zmiana przyłącza na trzy fazy.`)
    }
    if (ocena.tryb === 'wniosek') {
      dodaj('moc', `Instalacja (${liczbaPl(ocena.lacznie)} kW) większa od mocy przyłączeniowej (${liczbaPl(ocena.przylaczeniowa)} kW): `
        + 'zamiast zgłoszenia potrzebny wniosek do operatora o określenie warunków przyłączenia.')
    }
  } else if (jednaFaza) {
    dodaj('fazy', `Przyłącze jednofazowe: instalacja może mieć najwyżej ${liczbaPl(LIMIT_JEDNOFAZOWY_KW)} kW. Przy większej zmiana przyłącza na trzy fazy.`)
  }

  if (m.kable.droga === 'kanal') dodaj('kanal', 'Kanał wentylacyjny: czy jest naprawdę nieużywany i czy można nim poprowadzić przewody.')
  if (m.kable.droga === 'elewacja') dodaj('elewacja', 'Przewody po ścianie domu: trasa i osłona przewodów.')
  if (m.wifi.zasieg === 'slaby') dodaj('wifi', 'Słabe Wi-Fi przy falowniku: wzmacniacz sygnału albo przewód sieciowy.')
  if (m.wifi.zasieg === 'brak') dodaj('wifi', 'Brak Wi-Fi przy falowniku: wzmacniacz sygnału albo przewód sieciowy.')
  return punkty
}

/**
 * Lista „do sprawdzenia na miejscu”: `{ klucz, tekst, powod }`, gdzie `powod` to `nie_wiem` (klient nie wie),
 * `brak` (bez odpowiedzi), `z_odpowiedzi` albo `staly`. Kolejność: pola formularza, punkty z odpowiedzi, stałe.
 */
export const doSprawdzenia = (miejsca, kontekst = {}) => {
  const m = normalizujMiejsca(miejsca)
  const zPol = polaAktywne(m, kontekst)
    .filter((p) => m[p.czesc][p.pole] === NIE_WIEM || pusty(m[p.czesc][p.pole]) || bledny(m, p))
    .map((p) => ({
      klucz: klucz(p),
      tekst: bledny(m, p) ? `${p.etykieta} (wpis do poprawy)` : p.etykieta,
      powod: m[p.czesc][p.pole] === NIE_WIEM ? NIE_WIEM : 'brak',
    }))
  return [...zPol, ...punktyZOdpowiedzi(m, kontekst), ...STALE_PUNKTY.map((p) => ({ ...p, powod: 'staly' }))]
}

/** Jedna odpowiedź jako tekst; „nie wiem” i brak odpowiedzi dają null (trafiają do „do sprawdzenia”). */
const tekst = (m, czesc, pole) => {
  const def = POLA.find((p) => p.czesc === czesc && p.pole === pole)
  const v = m[czesc][pole]
  if (pusty(v) || v === NIE_WIEM || bledny(m, def)) return null
  return def.opcje ? nazwa(def.opcje, v) : v.trim()
}
const zdanie = (czesci) => {
  const t = czesci.filter(Boolean).join(', ')
  return t ? zDuzej(t.toLowerCase()) : ''
}

/**
 * Wiersze na kartkę „Miejsca w domu i do sprawdzenia”: `{ klucz, tytul, tekst, opis, sprawdzic }`, gdzie `sprawdzic`
 * to etykiety pól z „nie wiem” albo bez odpowiedzi w tym wierszu. Magazyn tylko, gdy obowiązuje.
 */
export const wierszeMiejsc = (miejsca, kontekst = {}) => {
  const m = normalizujMiejsca(miejsca)
  const aktywne = polaAktywne(m, kontekst)
  const sprawdzic = (czesc) => aktywne.filter((p) => p.czesc === czesc && tekst(m, p.czesc, p.pole) == null).map((p) => p.etykieta)
  const op = m.przylacze
  const wnetrze = zdanie([tekst(m, 'licznik', 'pomieszczenie'), tekst(m, 'licznik', 'kondygnacja')]).toLowerCase()
  const wiersze = [
    { klucz: 'licznik', tytul: 'Licznik',
      tekst: m.licznik.gdzie === 'wewnatrz'
        ? `W środku budynku${wnetrze ? `: ${wnetrze}` : ''}`
        : tekst(m, 'licznik', 'gdzie') || '' },
    { klucz: 'rozdzielnica', tytul: 'Rozdzielnica główna',
      tekst: zdanie([tekst(m, 'rozdzielnica', 'pomieszczenie'), tekst(m, 'rozdzielnica', 'kondygnacja'), tekst(m, 'rozdzielnica', 'sciana')]) },
    { klucz: 'falownik', tytul: 'Falownik',
      tekst: zdanie([tekst(m, 'falownik', 'miejsce'), tekst(m, 'falownik', 'odleglosc') && `${tekst(m, 'falownik', 'odleglosc')} od rozdzielnicy`]) },
    ...(aktywne.some((p) => p.czesc === 'magazyn')
      ? [{ klucz: 'magazyn', tytul: 'Magazyn energii', tekst: tekst(m, 'magazyn', 'miejsce') || '' }] : []),
    { klucz: 'przylacze', tytul: 'Przyłącze',
      tekst: [tekst(m, 'przylacze', 'fazy'), mocPrzylaczeniowaKw(m) && `moc przyłączeniowa ${liczbaPl(mocPrzylaczeniowaKw(m))} kW`,
        tekst(m, 'przylacze', 'operator'), tekst(m, 'przylacze', 'ppe') && `PPE ${cyfryPpe(op.ppe)}`].filter(Boolean).join(', ') },
    { klucz: 'kable', tytul: 'Kable z dachu', tekst: tekst(m, 'kable', 'droga') || '' },
    { klucz: 'wifi', tytul: 'Wi-Fi przy falowniku', tekst: tekst(m, 'wifi', 'zasieg') || '' },
  ]
  return wiersze.map((w) => ({ ...w, opis: (m[w.klucz].opis || '').trim(), sprawdzic: sprawdzic(w.klucz) }))
}

/**
 * Odpowiedzi w kształcie stanu formularza zawiadomienia do PSP (`PUSTY_STAN` w `lib/zawiadomieniePsp`), żeby klient
 * nie wpisywał ich drugi raz. Tylko to, co wiadomo: „nie wiem” i brak odpowiedzi zostawiają podpowiedź formularza.
 * Strona `/zawiadomienie-psp` przyjmuje to w stanie nawigacji (`state.klient`), jak dane z karty klienta.
 */
export const miejscaDoPsp = (miejsca) => {
  const m = normalizujMiejsca(miejsca)
  const s = {}
  if (MIEJSCA_FALOWNIKA.some(([k]) => k === m.falownik.miejsce)) s.miejsceFalownika = m.falownik.miejsce
  if (!pusty(m.falownik.opis)) s.miejsceFalownikaOpis = m.falownik.opis.trim()
  if (MIEJSCA_MAGAZYNU.some(([k]) => k === m.magazyn.miejsce)) s.pomieszczenieMagazynu = m.magazyn.miejsce
  if (!pusty(m.magazyn.opis)) s.miejsceMagazynu = m.magazyn.opis.trim()
  if (STRONY.some(([k]) => k === m.rozdzielnica.sciana)) s.planRozdzielnica = m.rozdzielnica.sciana
  const trasa = { poddasze: 'wewnatrz', kanal: 'wewnatrz', elewacja: 'elewacja' }[m.kable.droga]
  if (trasa) s.planTrasaDc = trasa
  return s
}

/** Kod PPE i moc przyłączeniowa w kształcie danych klienta zgłoszenia do OSD (`state.klient` strony zgłoszenia). */
export const miejscaDoOsd = (miejsca) => {
  const m = normalizujMiejsca(miejsca)
  const s = {}
  if (ppePoprawne(m.przylacze.ppe)) s.ppe = cyfryPpe(m.przylacze.ppe)
  if (mocPrzylaczeniowaKw(m)) s.mocPrzylaczeniowa = String(m.przylacze.moc).trim()
  return s
}

/**
 * Co audyt bierze z Projektu 3D (część `projekt` teczki): magazyn, gdy klient go wybrał w modelu, i podpowiedź miejsca
 * falownika. Tylko podpowiedź, nie odpowiedź: model ma domyślnie falownik na zewnątrz, nawet gdy klient tego kroku
 * nie oglądał, więc klient potwierdza ją jednym kliknięciem. Falownik w środku nie mówi, w którym pomieszczeniu.
 */
export const zProjektu = (projekt) => {
  if (!projekt || typeof projekt !== 'object') return { zMagazynem: undefined, falownik: null }
  const g = projekt.magazyn || {}
  const zMagazynem = g.model || Number(g.pojemnosc) > 0 ? true : undefined
  const f = projekt.falownik || {}
  const falownik = f.miejsce === 'zewnatrz' ? (projekt.rodzaj === 'grunt' ? 'konstrukcja' : 'sciana') : null
  return { zMagazynem, falownik }
}

const ODLEGLOSC_M = { do_5: 5, do_10: 10, do_15: 15 }
const wartosc = (v) => (pusty(v) || v === NIE_WIEM ? null : v)

/**
 * Dane audytu dla arkuszy teczki (schemat instalacji i rozmieszczenie urządzeń, pakiet P6) i dla widoku wykonawcy
 * (pakiet P5). Kody urządzeń jak na planie dla ekip ratowniczych (`planRatowniczy.js`): ZK złącze z licznikiem,
 * RG rozdzielnica główna, F falownik, M magazyn. `null` znaczy „nie wiadomo”; `doSprawdzenia` mówi, że klient
 * zaznaczył „nie wiem” albo nie odpowiedział. Stronę świata ma tylko rozdzielnica; stronę falownika ma model 3D.
 */
export const audytDoRysunku = (miejsca, kontekst = {}) => {
  const m = normalizujMiejsca(miejsca)
  const sprawdzic = new Set(doSprawdzenia(m, kontekst).map((p) => p.klucz))
  const opis = (czesc) => m[czesc].opis.trim() || null
  const urzadzenia = [
    { kod: 'ZK', nazwa: 'Złącze z licznikiem', gdzie: wartosc(m.licznik.gdzie), opis: opis('licznik'),
      doSprawdzenia: sprawdzic.has('licznik.gdzie') },
    { kod: 'RG', nazwa: 'Rozdzielnica główna', pomieszczenie: wartosc(m.rozdzielnica.pomieszczenie),
      strona: wartosc(m.rozdzielnica.sciana), opis: opis('rozdzielnica'),
      doSprawdzenia: sprawdzic.has('rozdzielnica.pomieszczenie') || sprawdzic.has('rozdzielnica.sciana') },
    { kod: 'F', nazwa: 'Falownik', miejsce: wartosc(m.falownik.miejsce), opis: opis('falownik'),
      doSprawdzenia: sprawdzic.has('falownik.miejsce') },
  ]
  if (polaAktywne(m, kontekst).some((p) => p.czesc === 'magazyn')) {
    urzadzenia.push({ kod: 'M', nazwa: 'Magazyn energii', miejsce: wartosc(m.magazyn.miejsce), opis: opis('magazyn'),
      doSprawdzenia: sprawdzic.has('magazyn.miejsce') })
  }
  const odleglosc = wartosc(m.falownik.odleglosc)
  const droga = wartosc(m.kable.droga)
  const fazy = wartosc(m.przylacze.fazy)
  return {
    wersja: WERSJA_MIEJSC,
    urzadzenia,
    przewodAc: { od: 'F', do: 'RG', doM: ODLEGLOSC_M[odleglosc] ?? null, ponad15: odleglosc === 'wiecej' },
    kableZDachu: droga,
    trasaDc: droga ? (droga === 'elewacja' ? 'elewacja' : 'wewnatrz') : null,
    fazy: fazy ? Number(fazy) : null,
    mocPrzylaczeniowaKw: mocPrzylaczeniowaKw(m),
    wifi: wartosc(m.wifi.zasieg),
  }
}

/**
 * Zapas w przeglądarce do czasu zapisu na koncie: `vv:teczka` to obiekt części teczki (umowa z sesją zapisu;
 * bez `projekt`, który ma własny klucz). Zapis zmienia tylko `miejsca`, pozostałe części zostają.
 */
export const KLUCZ_TECZKI = 'vv:teczka'

const czytajTeczke = () => {
  try {
    const t = JSON.parse(window.localStorage.getItem(KLUCZ_TECZKI) || '{}')
    return t && typeof t === 'object' && !Array.isArray(t) ? t : {}
  } catch (e) {
    return {}
  }
}

export const wczytajMiejsca = () => normalizujMiejsca(czytajTeczke().miejsca)

export const zapiszMiejsca = (miejsca) => {
  try {
    window.localStorage.setItem(KLUCZ_TECZKI, JSON.stringify({ ...czytajTeczke(), miejsca: normalizujMiejsca(miejsca) }))
    return true
  } catch (e) {
    return false
  }
}
